/**
 * /group/catalog — cross-business product catalog.
 *
 * Option A (replicate) — each Product row is independent per-business.
 * The page gives a flat view across every visible business + a bulk-create
 * wizard that atomically creates N Product rows across N businesses.
 *
 * Access: tenant_owner, super_admin, business_admin.
 *
 * Day 14 §5.3.3 Group Products.
 */
import { useState, useMemo, useEffect } from "react";
import {
  Boxes,
  Plus,
  Search,
  Filter,
  Package2,
  AlertTriangle,
  XCircle,
  Building2,
  CircleDollarSign,
  RefreshCcw,
  Image as ImageIcon,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  useGroupCatalog,
  useCatalogStats,
  useCatalogCategories,
  useBulkCreateProducts,
} from "@/hooks/useGroupProducts";
import { useMyBusinesses } from "@/hooks/useBusinesses";
import { curSymbol, formatMajor } from "@/lib/currency";

// ─── helpers ──────────────────────────────────────────────────────────

interface BusinessOption {
  _id: string;
  name: string;
}

function normalizeBusinesses(raw: unknown): BusinessOption[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((b) => {
      const o = b as Record<string, unknown>;
      const _id =
        (o._id as string) || (o.id as string) || (o.businessId as string) || "";
      const name =
        (o.name as string) || (o.businessName as string) || "Unnamed";
      return _id ? { _id, name } : null;
    })
    .filter((x): x is BusinessOption => Boolean(x))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function formatGHS(amount: number): string {
  return formatMajor(amount || 0);
}

function pesewasToGHS(minor: number | undefined): string {
  return formatGHS((minor || 0) / 100);
}

// ─── page ─────────────────────────────────────────────────────────────

export default function GroupCatalogPage() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [businessFilter, setBusinessFilter] = useState<string>("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data: businessesRaw } = useMyBusinesses();
  const businesses = useMemo(
    () => normalizeBusinesses(businessesRaw),
    [businessesRaw],
  );

  const {
    data: catalog,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useGroupCatalog({
    search: search || undefined,
    businessId: businessFilter || undefined,
    category: categoryFilter || undefined,
    lowStockOnly,
  });
  const { data: stats } = useCatalogStats();
  const { data: categories = [] } = useCatalogCategories();

  const items = catalog?.items || [];

  const hasFilters =
    Boolean(search) ||
    Boolean(businessFilter) ||
    Boolean(categoryFilter) ||
    lowStockOnly;

  function clearFilters() {
    setSearchInput("");
    setSearch("");
    setBusinessFilter("");
    setCategoryFilter("");
    setLowStockOnly(false);
  }

  return (
    <div className="min-h-screen bg-background">
      {/* ─── Hero ─── */}
      <div className="bg-gradient-to-br from-[#0A2540] to-[#0E2C44] text-white relative overflow-hidden">
        <div
          className="absolute -top-20 right-1/4 h-40 w-[36rem] rounded-full bg-[#4CAF50]/20 blur-3xl"
          aria-hidden
        />
        <div className="max-w-6xl mx-auto px-6 py-8 relative">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-[#7BAEC8] mb-1">
                <Boxes className="h-3 w-3" />
                Group catalog
              </div>
              <h1 className="text-2xl md:text-3xl font-semibold text-white">
                Products across all businesses
              </h1>
              <p className="text-sm text-[#A0BBCB] mt-1">
                Manage your catalog in one place. Add a product to many
                businesses at once.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => refetch()}
                disabled={isFetching}
                className="text-white/80 hover:text-white hover:bg-white/15"
              >
                <RefreshCcw
                  className={`h-3.5 w-3.5 mr-2 ${
                    isFetching ? "animate-spin" : ""
                  }`}
                />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => setBulkOpen(true)}
                className="bg-[#4CAF50] hover:bg-[#45A049] text-white"
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add to businesses
              </Button>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-6">
            <HeroStat
              icon={<Package2 className="h-3.5 w-3.5" />}
              label="Total products"
              value={stats?.totalSkus ?? 0}
              sub={stats ? `${stats.uniqueProductNames} unique` : undefined}
            />
            <HeroStat
              icon={<AlertTriangle className="h-3.5 w-3.5" />}
              label="Low stock"
              value={stats?.lowStockCount ?? 0}
              accent={(stats?.lowStockCount || 0) > 0 ? "amber" : undefined}
            />
            <HeroStat
              icon={<XCircle className="h-3.5 w-3.5" />}
              label="Out of stock"
              value={stats?.outOfStockCount ?? 0}
              accent={(stats?.outOfStockCount || 0) > 0 ? "rose" : undefined}
            />
            <HeroStat
              icon={<CircleDollarSign className="h-3.5 w-3.5" />}
              label="Inventory value"
              value={formatGHS(stats?.totalInventoryValueCedis || 0)}
              isText
            />
          </div>
        </div>
      </div>

      {/* ─── Body ─── */}
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="rounded-lg border bg-card p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
            <div className="md:col-span-4">
              <Label className="text-xs font-medium text-muted-foreground">
                Search
              </Label>
              <div className="relative mt-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Name, SKU, description…"
                  className="pl-8"
                />
              </div>
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs font-medium text-muted-foreground">
                Business
              </Label>
              <Select
                value={businessFilter || "__all__"}
                onValueChange={(v) =>
                  setBusinessFilter(v === "__all__" ? "" : v)
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="All businesses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All businesses</SelectItem>
                  {businesses.map((b) => (
                    <SelectItem key={b._id} value={b._id}>
                      {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs font-medium text-muted-foreground">
                Category
              </Label>
              <Select
                value={categoryFilter || "__all__"}
                onValueChange={(v) =>
                  setCategoryFilter(v === "__all__" ? "" : v)
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Any category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Any category</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2 flex items-center gap-2 pt-5">
              <Checkbox
                id="lowstock"
                checked={lowStockOnly}
                onCheckedChange={(v) => setLowStockOnly(Boolean(v))}
              />
              <Label
                htmlFor="lowstock"
                className="text-sm font-normal cursor-pointer"
              >
                Low stock only
              </Label>
            </div>
          </div>
          {hasFilters && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Filter className="h-3 w-3" />
                <span>
                  Filtered: {items.length} product
                  {items.length === 1 ? "" : "s"}
                </span>
              </div>
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          )}
        </div>

        {/* Per-business mini-bar */}
        {stats && stats.perBusiness.length > 1 && !hasFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {stats.perBusiness.map((b) => (
              <button
                key={b.businessId}
                onClick={() => setBusinessFilter(b.businessId)}
                className="rounded-md border bg-card p-3 text-left transition-colors hover:border-[#4CAF50]/40 hover:bg-[#4CAF50]/5"
              >
                <div className="flex items-center gap-1.5 text-xs font-medium text-[#0A2540] truncate">
                  <Building2 className="h-3 w-3 shrink-0" />
                  <span className="truncate">{b.businessName}</span>
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-lg font-semibold tabular-nums">
                    {b.skus}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    {b.skus === 1 ? "product" : "products"}
                  </span>
                </div>
                {b.lowStock > 0 && (
                  <div className="text-[10px] text-amber-700 mt-0.5">
                    {b.lowStock} low stock
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Table */}
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-2.5 bg-muted/50 text-xs font-medium text-muted-foreground uppercase tracking-wide border-b">
            <div className="col-span-4">Product</div>
            <div className="col-span-2">Business</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-1 text-right">Stock</div>
            <div className="col-span-2 text-right">Price</div>
            <div className="col-span-1 text-right">Status</div>
          </div>

          {isLoading && (
            <div className="py-12 text-center text-sm text-muted-foreground">
              Loading catalog…
            </div>
          )}

          {isError && (
            <div className="py-12 text-center">
              <AlertTriangle className="h-6 w-6 text-destructive mx-auto mb-2" />
              <p className="text-sm">Couldn't load catalog.</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => refetch()}
              >
                Retry
              </Button>
            </div>
          )}

          {!isLoading && !isError && items.length === 0 && (
            <div className="py-12 text-center">
              <Package2 className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm font-medium">No products found</p>
              <p className="text-xs text-muted-foreground mt-1">
                {hasFilters
                  ? "Try clearing the filters above."
                  : "Add your first product across multiple businesses."}
              </p>
              {!hasFilters && (
                <Button
                  size="sm"
                  onClick={() => setBulkOpen(true)}
                  className="mt-4 bg-[#4CAF50] hover:bg-[#45A049] text-white"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add products
                </Button>
              )}
            </div>
          )}

          {!isLoading && !isError && items.length > 0 && (
            <ul className="divide-y">
              {items.map((item) => (
                <li
                  key={item.product._id}
                  className="grid grid-cols-1 md:grid-cols-12 gap-3 px-4 py-3 items-center"
                >
                  {/* Product name + sku */}
                  <div className="md:col-span-4 flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                      {item.product.image ? (
                        <img
                          src={item.product.image}
                          alt={item.product.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <ImageIcon className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">
                        {item.product.name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {item.product.sku || "—"}
                      </p>
                    </div>
                  </div>

                  {/* Business */}
                  <div className="md:col-span-2">
                    <Badge
                      variant="outline"
                      className="font-normal text-xs gap-1"
                    >
                      <Building2 className="h-3 w-3" />
                      <span className="truncate max-w-[100px]">
                        {item.businessName}
                      </span>
                    </Badge>
                  </div>

                  {/* Category */}
                  <div className="md:col-span-2 text-xs text-muted-foreground truncate">
                    {item.product.category || "—"}
                  </div>

                  {/* Stock */}
                  <div className="md:col-span-1 text-sm tabular-nums text-right">
                    {item.product.quantity ?? 0}
                  </div>

                  {/* Price */}
                  <div className="md:col-span-2 text-sm tabular-nums text-right">
                    {pesewasToGHS(item.product.unitPriceMinor)}
                  </div>

                  {/* Status */}
                  <div className="md:col-span-1 flex justify-end">
                    {item.isOutOfStock ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] border-rose-500/40 bg-rose-50 text-rose-700"
                      >
                        Out
                      </Badge>
                    ) : item.isLowStock ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] border-amber-500/40 bg-amber-50 text-amber-700"
                      >
                        Low
                      </Badge>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">
                        OK
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <BulkCreateDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        businesses={businesses}
      />
    </div>
  );
}

// ─── Hero stat ────────────────────────────────────────────────────────

function HeroStat({
  icon,
  label,
  value,
  sub,
  accent,
  isText,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  sub?: string;
  accent?: "amber" | "rose";
  isText?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[#A0BBCB]">
        {icon}
        {label}
      </div>
      <div
        className={`mt-1.5 ${isText ? "text-xl" : "text-3xl"} font-bold ${
          accent === "amber"
            ? "text-amber-300"
            : accent === "rose"
              ? "text-rose-300"
              : "text-white"
        } ${!isText ? "tabular-nums" : ""}`}
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-[#A0BBCB]">{sub}</div>}
    </div>
  );
}

// ─── Bulk-create dialog ──────────────────────────────────────────────

interface BulkCreateDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  businesses: BusinessOption[];
}

function BulkCreateDialog({
  open,
  onOpenChange,
  businesses,
}: BulkCreateDialogProps) {
  const [selectedBusinessIds, setSelectedBusinessIds] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [sku, setSku] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [cost, setCost] = useState<string>("");
  const [sellPrice, setSellPrice] = useState<string>("");
  const [threshold, setThreshold] = useState<string>("");
  const [quantity, setQuantity] = useState<string>("");
  const [image, setImage] = useState("");

  const bulkCreate = useBulkCreateProducts();

  function reset() {
    setSelectedBusinessIds([]);
    setName("");
    setSku("");
    setDescription("");
    setCategory("");
    setCost("");
    setSellPrice("");
    setThreshold("");
    setQuantity("");
    setImage("");
  }

  function close(o: boolean) {
    if (!o) reset();
    onOpenChange(o);
  }

  function toggleBusiness(id: string) {
    setSelectedBusinessIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function selectAll() {
    setSelectedBusinessIds(businesses.map((b) => b._id));
  }

  function clearAll() {
    setSelectedBusinessIds([]);
  }

  const canSubmit =
    selectedBusinessIds.length > 0 &&
    name.trim().length > 0 &&
    !bulkCreate.isPending;

  async function onSubmit() {
    try {
      const result = await bulkCreate.mutateAsync({
        businessIds: selectedBusinessIds,
        product: {
          name: name.trim(),
          sku: sku.trim() || undefined,
          description: description.trim() || undefined,
          category: category.trim() || undefined,
          cost: cost ? Number(cost) : undefined,
          sellPriceCedis: sellPrice ? Number(sellPrice) : undefined,
          threshold: threshold ? Number(threshold) : undefined,
          quantity: quantity ? Number(quantity) : undefined,
          image: image.trim() || undefined,
        },
      });
      toast.success(
        `Created in ${result.count} business${result.count === 1 ? "" : "es"}`,
      );
      close(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || "Failed to create");
    }
  }

  const allSelected =
    businesses.length > 0 && selectedBusinessIds.length === businesses.length;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add product to multiple businesses</DialogTitle>
          <DialogDescription>
            One form, many businesses. Each business gets its own copy — you can
            edit prices and stock per business afterwards.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Step 1: pick businesses */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-semibold">
                1. Pick businesses
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {selectedBusinessIds.length} of {businesses.length} selected
                </span>
              </Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={allSelected ? clearAll : selectAll}
                >
                  {allSelected ? "Clear all" : "Select all"}
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {businesses.length === 0 && (
                <div className="col-span-2 text-center py-4 text-sm text-muted-foreground border-2 border-dashed rounded-md">
                  No businesses available.
                </div>
              )}
              {businesses.map((b) => {
                const checked = selectedBusinessIds.includes(b._id);
                return (
                  <label
                    key={b._id}
                    className={`flex items-center gap-2 p-2.5 rounded-md border cursor-pointer transition-colors ${
                      checked
                        ? "bg-[#4CAF50]/5 border-[#4CAF50]/40"
                        : "bg-card hover:bg-muted/40"
                    }`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleBusiness(b._id)}
                    />
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-sm truncate">{b.name}</span>
                  </label>
                );
              })}
            </div>
          </section>

          {/* Step 2: product fields */}
          <section className="space-y-3 border-t pt-4">
            <Label className="text-sm font-semibold">2. Product details</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="bc-name">
                  Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="bc-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Bag of cement, 50kg"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bc-sku">SKU (optional)</Label>
                <Input
                  id="bc-sku"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  placeholder="CEM-50"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bc-category">Category</Label>
                <Input
                  id="bc-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Construction materials"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="bc-desc">Description</Label>
                <Textarea
                  id="bc-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional"
                  rows={2}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bc-cost">Cost ({curSymbol()})</Label>
                <Input
                  id="bc-cost"
                  type="number"
                  min="0"
                  step="0.01"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  placeholder="50.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bc-price">Sell price ({curSymbol()})</Label>
                <Input
                  id="bc-price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={sellPrice}
                  onChange={(e) => setSellPrice(e.target.value)}
                  placeholder="85.00"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bc-qty">Opening quantity</Label>
                <Input
                  id="bc-qty"
                  type="number"
                  min="0"
                  step="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  placeholder="0"
                />
                <p className="text-[10px] text-muted-foreground">
                  Defaults to 0. Add real stock via Inventory Receipts per
                  business so journals post correctly.
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="bc-threshold">Low-stock threshold</Label>
                <Input
                  id="bc-threshold"
                  type="number"
                  min="0"
                  step="1"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  placeholder="5"
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="bc-image">Image URL (optional)</Label>
                <Input
                  id="bc-image"
                  value={image}
                  onChange={(e) => setImage(e.target.value)}
                  placeholder="https://…/cement.jpg"
                />
              </div>
            </div>
          </section>

          {/* Preview */}
          {selectedBusinessIds.length > 0 && name.trim() && (
            <section className="border-t pt-4">
              <div className="rounded-md border bg-[#4CAF50]/5 border-[#4CAF50]/30 p-3">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-[#4CAF50] shrink-0 mt-0.5" />
                  <p className="text-sm">
                    Will create <strong>“{name.trim()}”</strong> in{" "}
                    <strong>{selectedBusinessIds.length}</strong> business
                    {selectedBusinessIds.length === 1 ? "" : "es"}. Each gets
                    its own independent row — prices and stock can diverge.
                  </p>
                </div>
              </div>
            </section>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => close(false)}>
            Cancel
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!canSubmit}
            className="bg-[#4CAF50] hover:bg-[#45A049] text-white"
          >
            {bulkCreate.isPending
              ? "Creating…"
              : `Create in ${selectedBusinessIds.length} business${
                  selectedBusinessIds.length === 1 ? "" : "es"
                }`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
